// ============================================================
// Bildirim merkezi (§2)
//
// Header'daki zil ikonu artık gerçek: açılır panelde türetilen
// bildirimler, okundu işaretleme ve kanal gönderimi var.
// ============================================================

import { emptyNote } from "./ui.js";
import { esc } from "../lib/fmt.js";
import { KANALLAR, ONEM, bildirimler, gonderilebilirKanallar, gonderimSayisi, okunmamisSayisi } from "../domain/notifications.js";
import { B } from "../domain/context.js";
import { state } from "../state.js";

const TIP_IKON = {
  gelmedi: "🚫", binada: "🏫", olay: "🩹", ilac: "💊", belge: "📄",
  riza: "🔏", tahsilat: "🧾", kontenjan: "🎒", etkinlik: "🎈",
  rapor: "📝", cikis: "👋", fatura: "💳",
};

export function bildirimRozeti() {
  const sayi = okunmamisSayisi();
  if (!sayi) return "";
  return `<span class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-coral text-white
    text-[10px] font-bold grid place-items-center">${sayi > 9 ? "9+" : sayi}</span>`;
}

export function bildirimPaneli() {
  const list = bildirimler();
  const okunmamis = list.filter(b => !b.okundu).length;

  return `
  <div class="px-4 py-3 border-b border-line flex items-center gap-2">
    <span class="font-display font-bold text-sm">Bildirimler</span>
    ${okunmamis ? `<span class="text-[10px] font-bold rounded-full bg-coral text-white px-2 py-0.5">${okunmamis} yeni</span>` : ""}
    ${list.length ? `<button data-notif-allread class="ml-auto text-[11px] font-semibold text-teal hover:underline">Tümünü okundu işaretle</button>` : ""}
  </div>

  <div class="max-h-[420px] overflow-y-auto">
    ${list.length ? list.map(satir).join("") : emptyNote("Bekleyen bildirim yok.")}
  </div>

  <div class="px-4 py-2 border-t border-line text-[10px] text-inksoft">
    Bildirimler veriden türetilir — durum düzelince kendiliğinden kaybolur.
  </div>`;
}

function satir(b) {
  const o = ONEM[b.onem];
  const kanallar = gonderilebilirKanallar(b, B().id);
  const gonderim = gonderimSayisi(b.key, B().id);

  return `
  <div class="px-4 py-3 border-b border-line/70 ${b.okundu ? "opacity-60" : ""} hover:bg-paper/60">
    <div class="flex items-start gap-2.5">
      <span class="shrink-0 mt-0.5">${TIP_IKON[b.tip] ?? "🔔"}</span>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5">
          <span class="text-sm font-semibold truncate">${esc(b.baslik)}</span>
          ${!b.okundu ? `<span class="w-1.5 h-1.5 rounded-full bg-${o.renk} shrink-0"></span>` : ""}
        </div>
        <p class="text-[11px] text-inksoft mt-0.5 leading-snug">${esc(b.metin)}</p>

        <div class="flex items-center gap-1.5 mt-2 flex-wrap">
          ${b.hedef ? `<button data-notif-go="${esc(b.hedef)}" class="text-[10px] font-semibold rounded-lg border border-line bg-white px-2 py-1 hover:bg-paper">Aç</button>` : ""}
          ${!b.okundu ? `<button data-notif-read="${esc(b.key)}" class="text-[10px] font-semibold rounded-lg border border-line bg-white px-2 py-1 hover:bg-paper">Okundu</button>` : ""}
          <button data-notif-dismiss="${esc(b.key)}" class="text-[10px] font-semibold rounded-lg border border-line bg-white px-2 py-1 hover:bg-paper text-inksoft">Kapat</button>

          ${kanallar.map(k => `
            <button data-notif-send="${esc(b.key)}:${k}"
              class="text-[10px] font-semibold rounded-lg px-2 py-1 transition
                ${KANALLAR[k].hazir ? "bg-ink text-white hover:bg-inksoft" : "border border-line bg-white text-inksoft"}"
              title="${KANALLAR[k].hazir ? `${KANALLAR[k].label} ile gönder` : `${KANALLAR[k].label} entegrasyonu kurulmadı — kayıt üretilir, mesaj gitmez`}">
              ${KANALLAR[k].icon} ${KANALLAR[k].label}${KANALLAR[k].hazir ? "" : " ·kurulmadı"}
            </button>`).join("")}

          ${gonderim ? `<span class="text-[10px] text-teal font-semibold">✓ ${gonderim} gönderim</span>` : ""}
        </div>
      </div>
    </div>
  </div>`;
}

/** Ayarlar'daki kanal yapılandırma kartı. */
export function kanalKarti(branchId) {
  const t = B();
  const tercih = t.notificationChannels ?? {};

  return `
  <div class="p-5">
    <div class="flex items-center justify-between mb-3">
      <h2 class="font-display font-bold text-base">Bildirim Kanalları</h2>
      <span class="text-[11px] text-inksoft hidden sm:block">${esc(t.name)}</span>
    </div>

    <ul class="space-y-1.5">
      ${Object.entries(KANALLAR).map(([kod, k]) => {
        const acik = Boolean(tercih[kod]);
        return `
        <li class="flex items-center gap-3 rounded-xl border border-line px-3 py-2">
          <span class="shrink-0">${k.icon}</span>
          <span class="min-w-0 flex-1">
            <span class="block text-sm font-semibold">${esc(k.label)}</span>
            <span class="block text-[11px] text-inksoft">
              ${k.hazir ? "Kullanıma hazır" : "Entegrasyon kurulmadı"}
              ${k.ucret ? ` · mesaj başına ~${k.ucret} ₺` : " · ücretsiz"}
            </span>
          </span>
          <button data-notif-channel="${branchId}:${kod}"
            class="shrink-0 w-11 h-6 rounded-full transition relative ${acik ? "bg-teal" : "bg-line"}"
            role="switch" aria-checked="${acik}" aria-label="${esc(k.label)} kanalı">
            <span class="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${acik ? "left-[22px]" : "left-0.5"}"></span>
          </button>
        </li>`;
      }).join("")}
    </ul>

    <p class="text-[11px] text-inksoft mt-3">
      Kapalı kanal hiç denenmez. Açık ama entegrasyonu kurulmamış kanalda
      gönderim kaydı oluşur, mesaj iletilmez — gerçek servis bağlanınca
      geçmiş kayıp olmaz.
    </p>
  </div>`;
}

/** Panelin açık olup olmadığı state'te tutulur. */
export const panelAcik = () => state.notifOpen === true;
